import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loader2, Car, ShieldAlert, Sparkles, Layers, CheckCircle2, Clock, Plus } from 'lucide-react'
import { useAdminCarsOverview } from '../../../hooks/use-admin-cars'
import { Link } from 'react-router-dom'
import { appRoutes } from '@/constants'

export function CarsOverviewPage() {
  const { data: stats, isLoading, error } = useAdminCarsOverview()

  if (isLoading) {
    return (
      <div className="p-12 text-center flex flex-col items-center justify-center space-y-3">
        <Loader2 className="size-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading Cars Control Center metrics...</p>
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className="p-12 text-center text-rose-500">
        Failed to load Cars metrics. Please ensure backend is running.
      </div>
    )
  }

  const statCards = [
    { title: 'Total Brands', count: stats.totalBrands, icon: Car, href: appRoutes.admin.cars.brands, color: 'text-blue-500' },
    { title: 'Total Models', count: stats.totalModels, icon: Layers, href: appRoutes.admin.cars.models, color: 'text-indigo-500' },
    { title: 'Total Generations', count: stats.totalGenerations, icon: Sparkles, href: appRoutes.admin.cars.generations, color: 'text-purple-500' },
    { title: 'Total Variants', count: stats.totalVariants, icon: CheckCircle2, href: appRoutes.admin.cars.variants, color: 'text-emerald-500' },
    { title: 'Total Features', count: stats.totalFeatures, icon: Sparkles, href: appRoutes.admin.cars.features, color: 'text-amber-500' },
    { title: 'Total Listings', count: stats.totalListings, icon: Car, href: appRoutes.admin.cars.listings, color: 'text-cyan-500' },
    { title: 'Pending Moderation', count: stats.pendingListings, icon: Clock, href: appRoutes.admin.cars.listings, color: 'text-rose-500' },
    { title: 'Model Requests', count: stats.pendingModelRequests, icon: ShieldAlert, href: appRoutes.admin.cars.modelRequests, color: 'text-orange-500' },
  ]

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Cars Control Center</h1>
          <p className="text-sm text-muted-foreground">Manage canonical master data, inspect seller listings, and review moderation queues.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to={appRoutes.admin.cars.brands}>
            <Button size="sm" className="gap-2">
              <Plus className="size-4" /> Add Brand
            </Button>
          </Link>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, i) => (
          <Link key={i} to={card.href}>
            <Card className="p-5 hover:border-primary/50 transition-colors cursor-pointer space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{card.title}</span>
                <card.icon className={`size-5 ${card.color}`} />
              </div>
              <div className="font-display text-3xl font-extrabold text-foreground">{card.count}</div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Listings & Pending Queue */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-bold">Recently Submitted Vehicles</h3>
            <Link to={appRoutes.admin.cars.listings} className="text-xs font-semibold text-primary hover:underline">View All</Link>
          </div>
          <div className="space-y-3">
            {stats.recentListings.length > 0 ? (
              stats.recentListings.map((car: any) => (
                <div key={car.id} className="flex items-center justify-between p-3 rounded-xl border border-border bg-accent/20">
                  <div className="space-y-0.5">
                    <div className="font-bold text-sm">{car.title}</div>
                    <div className="text-xs text-muted-foreground">{car.brand?.name} • {car.user ? `${car.user.firstName} ${car.user.lastName}` : 'Seller'} ({car.user?.role})</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-sm">₹{Number(car.askingPrice).toLocaleString('en-IN')}</span>
                    <Badge className={car.status === 'PUBLISHED' ? 'bg-emerald-500/20 text-emerald-500' : car.status === 'PENDING_REVIEW' ? 'bg-amber-500/20 text-amber-500' : 'bg-rose-500/20 text-rose-500'}>
                      {car.status}
                    </Badge>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No recent vehicle listings found.</p>
            )}
          </div>
        </Card>

        {/* Quick Actions & Moderation Overview */}
        <Card className="p-6 space-y-4">
          <h3 className="font-display text-lg font-bold">Quick Master Data Actions</h3>
          <div className="space-y-2">
            <Link to={appRoutes.admin.cars.brands} className="block w-full">
              <Button variant="outline" className="w-full justify-start gap-2 text-xs font-semibold">
                <Car className="size-4 text-primary" /> Manage Brands Catalog
              </Button>
            </Link>
            <Link to={appRoutes.admin.cars.models} className="block w-full">
              <Button variant="outline" className="w-full justify-start gap-2 text-xs font-semibold">
                <Layers className="size-4 text-primary" /> Manage Models &amp; Generations
              </Button>
            </Link>
            <Link to={appRoutes.admin.cars.features} className="block w-full">
              <Button variant="outline" className="w-full justify-start gap-2 text-xs font-semibold">
                <Sparkles className="size-4 text-primary" /> Manage Feature Catalog
              </Button>
            </Link>
            <Link to={appRoutes.admin.cars.listings} className="block w-full">
              <Button variant="outline" className="w-full justify-start gap-2 text-xs font-semibold">
                <Clock className="size-4 text-primary" /> Moderation Queue ({stats.pendingListings})
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  )
}
